import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useAddressAPI } from '../hooks/useAddressAPI';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const initialAddressState = {
    options: {
        regions: [],
        provinces: [],
        cities: [],
        districts: [],
        barangays: [],
    },
    selected: {
        region: '',
        province: '',
        city: '',
        district: '',
        barangay: '',
        street: '',
    },
    manual: {
        province: false,
        city: false,
        barangay: false,
    }
};

const AddressPicker = ({ open, onClose, onAddressSelect, initialValue }) => {
    const { loading, getRegions, getProvinces, getCities, getBarangays, getDistricts } = useAddressAPI();
    const [address, setAddress] = useState(initialAddressState);

    useEffect(() => {
        if (open) {
            setAddress(initialAddressState);
            getRegions().then(regions => {
                setAddress(prev => ({ ...prev, options: { ...prev.options, regions } }));
            });
        }
    }, [open, getRegions]);

    useEffect(() => {
        if (address.selected.region) {
            getProvinces(address.selected.region).then(provinces => {
                setAddress(prev => ({
                    ...prev,
                    manual: {
                        ...prev.manual,
                        province: provinces.length === 0,
                        city: false,
                        barangay: false,
                    },
                    options: {
                        ...prev.options,
                        provinces,
                        cities: [],
                        districts: [],
                        barangays: [],
                    },
                    selected: {
                        ...prev.selected,
                        province: '',
                        city: '',
                        district: '',
                        barangay: '',
                    }
                }));
            });
        }
    }, [address.selected.region, getProvinces]);

    useEffect(() => {
        if (address.selected.province) {
            getCities(address.selected.province).then(cities => {
                setAddress(prev => ({
                    ...prev,
                    manual: {
                        ...prev.manual,
                        city: cities.length === 0,
                        barangay: false,
                    },
                    options: {
                        ...prev.options,
                        cities,
                        districts: [],
                        barangays: [],
                    },
                    selected: {
                        ...prev.selected,
                        city: '',
                        district: '',
                        barangay: '',
                    }
                }));
            });
        }
    }, [address.selected.province, getCities]);

    useEffect(() => {
        if (address.selected.city) {
            getDistricts(address.selected.city).then(async (districts) => {
                let barangays = [];

                // If no districts, fetch barangays directly from city
                if (districts.length === 0) {
                    barangays = await getBarangays(address.selected.city);
                }

                setAddress(prev => ({
                    ...prev,
                    manual: {
                        ...prev.manual,
                        barangay: districts.length === 0 && barangays.length === 0
                    },
                    options: {
                        ...prev.options,
                        districts,
                        barangays
                    },
                    selected: {
                        ...prev.selected,
                        district: '',
                        barangay: ''
                    }
                }));
            });
        }
    }, [address.selected.city, getBarangays, getDistricts]);

    useEffect(() => {
        if (address.selected.district) {
            getBarangays(address.selected.district).then(barangays => {
                setAddress(prev => ({
                    ...prev,
                    manual: {
                        ...prev.manual,
                        barangay: barangays.length === 0
                    },
                    options: {
                        ...prev.options,
                        barangays
                    },
                    selected: {
                        ...prev.selected,
                        barangay: ''
                    }
                }));
            });
        }
    }, [address.selected.district, getBarangays]);

    const handleConfirm = () => {
        const { street, region, province, city, district, barangay } = address.selected;
        const regionName = address.options.regions.find(r => r.code === region)?.name || '';
        const provinceName = address.options.provinces.find(p => p.code === province)?.name || '';
        const cityName = address.options.cities.find(c => c.code === city)?.name || '';
        const districtName = address.options.districts.find(d => d.code === district)?.name || '';
        const barangayName = address.options.barangays.find(b => b.code === barangay)?.name || '';

        const fullAddress = [street, barangayName, districtName, cityName, provinceName, regionName].filter(Boolean).join(' ');
        onAddressSelect(fullAddress);
        onClose();
    };

    const handleSelectChange = (field, value) => {
        setAddress(prev => ({ ...prev, selected: { ...prev.selected, [field]: value } }));
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="address-picker-title">
            <Box sx={modalStyle}>
                <h2 id="address-picker-title" className="text-lg font-semibold mb-4">Select Address</h2>
                {loading && <CircularProgress />}
                <div className="space-y-4" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.5 : 1 }}>
                    <Autocomplete
                        options={address.options.regions}
                        getOptionLabel={(option) => option.name || ''}
                        value={address.options.regions.find(r => r.code === address.selected.region) || null}
                        onChange={(event, newValue) => {
                            handleSelectChange('region', newValue ? newValue.code : '');
                        }}
                        renderInput={(params) => <TextField {...params} label="Region" />}
                    />

                    {address.manual.province ? (
                        <TextField
                            fullWidth
                            label="Please Input Province"
                            value={address.selected.province}
                            onChange={(e) => handleSelectChange('province', e.target.value)}
                        />
                    ) : <Autocomplete
                        options={address.options.provinces}
                        getOptionLabel={(option) => option.name || ''}
                        value={address.options.provinces.find(p => p.code === address.selected.province) || null}
                        onChange={(event, newValue) => {
                            handleSelectChange('province', newValue ? newValue.code : '');
                        }}
                        disabled={!address.selected.region || address.options.provinces.length === 0}
                        renderInput={(params) => <TextField {...params} label="Province" />}
                    />}

                    {address.manual.city ? (
                        <TextField
                            fullWidth
                            label="Please Input City / Municipality"
                            value={address.selected.city}
                            onChange={(e) => handleSelectChange('city', e.target.value)}
                        />
                    ) : <Autocomplete
                        options={address.options.cities}
                        getOptionLabel={(option) => option.name || ''}
                        value={address.options.cities.find(c => c.code === address.selected.city) || null}
                        onChange={(event, newValue) => {
                            handleSelectChange('city', newValue ? newValue.code : '');
                        }}
                        disabled={!address.selected.province || address.options.cities.length === 0}
                        renderInput={(params) => <TextField {...params} label="City / Municipality" />}
                    />}

                    {address.options.districts.length > 0 && (
                        <Autocomplete
                            options={address.options.districts}
                            getOptionLabel={(option) => option.name || ''}
                            value={address.options.districts.find(d => d.code === address.selected.district) || null}
                            onChange={(event, newValue) => {
                                handleSelectChange('district', newValue ? newValue.code : '');
                            }}
                            disabled={!address.selected.city}
                            renderInput={(params) => <TextField {...params} label="District" />}
                        />
                    )}

                    {address.manual.barangay ? (
                        <TextField
                            fullWidth
                            label="Please Input Barangay"
                            value={address.selected.barangay}
                            onChange={(e) => handleSelectChange('barangay', e.target.value)}
                        />
                    ) : <Autocomplete
                        options={address.options.barangays}
                        getOptionLabel={(option) => option.name || ''}
                        value={address.options.barangays.find(b => b.code === address.selected.barangay) || null}
                        onChange={(event, newValue) => {
                            handleSelectChange('barangay', newValue ? newValue.code : '');
                        }}
                        disabled={(!address.selected.city || (address.options.districts.length > 0 && !address.selected.district))}
                        renderInput={(params) => <TextField {...params} label="Barangay" />}
                    />}

                    <TextField
                        placeholder="House No., Street, Subdivision"
                        fullWidth
                        label="Street Address"
                        variant="outlined"
                        value={address.selected.street}
                        onChange={(e) => handleSelectChange('street', e.target.value)}
                        disabled={!address.selected.barangay}
                    />
                </div>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={!address.selected.street}
                    >
                        Confirm Address
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddressPicker;